import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export type KnowledgeTab = 'files' | 'texts' | 'faqs' | 'urls';

const TABS: ReadonlyArray<KnowledgeTab> = ['files', 'texts', 'faqs', 'urls'];

export type KnowledgeBaseOrdering =
  | '-created_at'
  | 'created_at'
  | 'name'
  | '-name'
  | '-updated_at';

/**
 * The page's list state, held in the URL so a refresh or a shared link lands
 * on the same narrowed list and the same tab. `q` (the base search) and
 * `search` (the content search) are written by `SearchInput` itself; this
 * hook only reads them.
 */
export function useKnowledgeBaseFilters() {
  const [params, setParams] = useSearchParams();

  const set = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (prev) => {
          Object.entries(patch).forEach(([key, value]) => {
            if (value === null) prev.delete(key);
            else prev.set(key, value);
          });
          return prev;
        },
        { replace: true }
      ),
    [setParams]
  );

  const connectedParam = params.get('connected');
  const activeParam = params.get('active');
  const tabParam = params.get('tab') as KnowledgeTab | null;

  return {
    q: params.get('q') ?? '',
    /** undefined: every base; true: has an agent; false: has none. */
    connected:
      connectedParam === 'true'
        ? true
        : connectedParam === 'false'
          ? false
          : undefined,
    /** undefined: every base; true or false narrows to that state. */
    isActive:
      activeParam === 'true'
        ? true
        : activeParam === 'false'
          ? false
          : undefined,
    ordering:
      (params.get('o') as KnowledgeBaseOrdering | null) ?? '-created_at',
    tab: tabParam && TABS.includes(tabParam) ? tabParam : 'files',
    search: params.get('search') ?? '',
    set,
  };
}
